import React from "react";

/**
 * Vincent B926 (Eric 2026-02-14 SPEC STRICK PROD) — Classes CSS
 * partagées entre les 3 toolbars Transcriptions (Whisper / Fireflies /
 * Fusionnée) pour distinguer visuellement les 3 groupes de boutons :
 *
 *   • GROUP_SETTINGS ("Réglages") — gris ardoise, indique une action
 *     de configuration (Réglages Whisper, Réglages Assembly AI,
 *     Réglages fusion).
 *   • GROUP_RERUN ("Refaire / Réappliquer / Réanalyser") — violet plein,
 *     indique une action lourde qui régénère du contenu (Refaire
 *     transcription Whisper, Réappliquer diarisation micro-tours,
 *     Réanalyser Fireflies, Refaire la fusion).
 *   • GROUP_UTIL (tous les autres boutons utilitaires) — ambre outline
 *     léger, indique une action ponctuelle sans impact majeur sur les
 *     données existantes (Nettoyer, Regrouper, Purger, Diagnostic,
 *     URLs, Historique, Rapport).
 *
 * Objectif Vincent : « fais une différence claire de style et de
 * couleur entre les 3 groupes de boutons » sur les 3 sous-onglets.
 */

const _BASE = "flex-shrink-0 text-[11px] font-semibold px-3 py-1 rounded inline-flex items-center gap-1.5";

export const GROUP_SETTINGS = `${_BASE} bg-slate-100 border border-slate-400 text-slate-800 hover:bg-slate-200`;

export const GROUP_RERUN = `${_BASE} bg-purple-600 text-white hover:bg-purple-700 shadow-sm`;

export const GROUP_UTIL = `${_BASE} bg-white border border-amber-300 text-amber-900 hover:bg-amber-100`;

// Variante « alerte » du groupe utilitaire (diagnostic suspect, purge
// destructive) — on garde la même famille outline pour ne pas rompre
// la hiérarchie mais avec une couleur d'urgence.
export const GROUP_UTIL_ALERT = `${_BASE} bg-white border-2 border-rose-500 text-rose-800 hover:bg-rose-50 ring-2 ring-rose-300 ring-offset-1`;

// Vincent B970 rev + B973 (Eric 2026-02-26 SPEC STRICK PROD) — Petit
// séparateur vertical pour grouper visuellement les blocs de boutons
// dans les toolbars Fireflies / Whisper / Merged (single-block). Rendu
// via un `<div>` (pas un `<hr>`) pour rester inline dans un flex-wrap.
export const TOOLBAR_SEPARATOR_CLASS =
  "w-px h-4 bg-zinc-300 mx-1 flex-shrink-0 self-center";

// Tâches 1306 · 1307 · 1308 · 1309 (Eric 2026-06-30) — Libellé de groupe
// (« Fusion : », « Orthographe : », « Whisper : », « Assembly AI : »,
// « Fireflies : »), au même format que « Source : » de la ligne 1. Il porte
// le mot que les boutons ne répètent plus : sans lui, « Réglages » et
// « Diagnostic » ne diraient plus de quoi ils parlent.
//
// Tâche 1312 — Le libellé devient le bouton qui replie son bloc. Un bloc
// replié annonce le nombre de boutons qu'il cache : un simple chevron
// laisserait croire qu'il n'y a rien derrière.
export const ToolbarGroupLabel = ({ children, testid, collapsed, onToggle, count }) => {
  const classes = "text-xs font-medium text-zinc-700 whitespace-nowrap flex-shrink-0";
  if (typeof onToggle !== "function") {
    return <span className={classes} data-testid={testid}>{children}</span>;
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`${classes} inline-flex items-center gap-1 rounded px-1 -mx-1 hover:bg-zinc-200/70 transition-colors`}
      data-testid={testid}
      data-collapsed={collapsed ? "true" : "false"}
      aria-expanded={collapsed ? "false" : "true"}
      title={collapsed
        ? "Déplier ce groupe de boutons"
        : "Replier ce groupe de boutons (le choix est mémorisé)"}
    >
      <span aria-hidden="true" className="text-[9px] text-zinc-500">
        {collapsed ? "▸" : "▾"}
      </span>
      {children}
      {collapsed && count ? (
        <span className="text-[10px] text-zinc-500 font-semibold">({count})</span>
      ) : null}
    </button>
  );
};


// Composant utilitaire (import: `import { ToolbarSeparator } from ...`).
// Rendu inline pour rester compatible avec les toolbars flex-wrap.
export const ToolbarSeparator = () => (
  <div
    className={TOOLBAR_SEPARATOR_CLASS}
    role="separator"
    aria-orientation="vertical"
    data-testid="toolbar-separator"
  />
);
